/**
 * LocaleGate + RedirectToDefaultLocale (issue #221 PR #2, Step 2).
 *
 * The storefront serves every chrome route under a `/:locale/...` prefix. These
 * two components own the URL contract at the router boundary:
 *
 *   - `RedirectToDefaultLocale` sends the root path and any non-localised
 *     bookmark to the store-default locale, preserving path + query + hash.
 *   - `LocaleGate` validates the `:locale` param against the per-store catalog
 *     and renders the nested routes (`<Outlet/>`) only for a supported,
 *     lowercase-canonical segment.
 *
 * The catalog (default + supported) is read synchronously from the already-loaded
 * runtime config — `App` gates rendering on config load, so `getCachedConfig()`
 * is populated by the time any route renders (no async gate, no flash).
 */

import React from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { readLocaleCatalog } from "./localeCatalog";
import {
	findSupportedLocale,
	localeToPathSegment,
	splitLocaleFromPath,
	withLocale,
} from "./localeRouting";

/**
 * Redirect the current (non-localised) location to the store-default locale,
 * preserving path + query + hash. Drives the root redirect (`/` → `/en-us`) and
 * bookmark migration (`/products` → `/en-us/products`). `replace` keeps the
 * pre-redirect entry out of history so the back button isn't polluted.
 */
export const RedirectToDefaultLocale: React.FC = () => {
	const location = useLocation();
	const { defaultLocale } = readLocaleCatalog();
	// Invariant: every call site (root `/`, the `*` catch-all, and LocaleGate's
	// unmatched-segment branch) only reaches here with a path that does NOT lead
	// with a supported locale, so `withLocale` prefixes (never double-prefixes)
	// and the redirect target always lands on a segment LocaleGate accepts — no loop.
	const target = `${withLocale(defaultLocale, location.pathname)}${location.search}${location.hash}`;
	return <Navigate to={target} replace />;
};

/**
 * Gate for the `/:locale` subtree. Renders the nested routes when `:locale` is a
 * supported, lowercase-canonical segment; otherwise:
 *   - an unknown segment (a non-localised bookmark like `/products`) redirects to
 *     the default-locale prefix;
 *   - a supported-but-miscased segment (`/sv-SE/...`) redirects to its canonical
 *     lowercase form (`/sv-se/...`) so crawlers don't see case-duplicate URLs.
 */
export const LocaleGate: React.FC = () => {
	const location = useLocation();
	const params = useParams();
	const { supported } = readLocaleCatalog();
	const segment = params.locale ?? "";
	const matched = findSupportedLocale(segment, supported);

	if (!matched) {
		return <RedirectToDefaultLocale />;
	}
	if (segment !== localeToPathSegment(matched)) {
		const { rest } = splitLocaleFromPath(location.pathname, supported);
		const target = `${withLocale(matched, rest)}${location.search}${location.hash}`;
		return <Navigate to={target} replace />;
	}
	return <Outlet />;
};

export default LocaleGate;
